export type UserStatus = 'Active' | 'Deactivated' | 'Inactive' | 'Unverified' | 'Pending' | 'Terminated';
export type UnitSystem = 'IMPERIAL' | 'METRIC';
export type PhoneType = 'MOBILE' | 'WORK';
export type AddressType = 'WORK' | 'HOME';
export type EmailType = 'WORK';

export enum UserType {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
}

export interface Email {
  primary: boolean;
  email_type: string;
  email_address: string;
}

export interface Address {
  address_1: string;
  address_2?: string;
  city: string;
  address_type: string;
  state_province: string;
  postal_code: string;
  country: string;
  country_name?: string;
  primary: boolean;
}

export interface Phone {
  primary: boolean;
  phone_type: string;
  phone_number: string;
  extension: string;
  country_code: string;
}

export interface Organization {
  primary: boolean;
  organization_id: string;
  industry: string;
  subindustry: string;
  legal_name: string;
  display_label: string;
  // Informational flag returned by Atlas: whether this org is a data provider
  // ("whitelisted"). Surfaced on the View page as "Primary Org is Data Provider".
  whitelisted?: boolean;
}

export interface User {
  enel_employee_id: string;
  flex_contact_id: string;
  username: string;
  userId: string;
  userType: string;
  userClass: string;
  first_names: Record<string, string>;
  title: string;
  fullName: string;
  last_names: Record<string, string>;
  brand: string;
  locale: string;
  email: string;
  phone_number: string;
  phones: Phone[];
  emails: Email[];
  timezone: string;
  countryCode: string;
  addresses: Address[];
  status: UserStatus;
  unit_system: UnitSystem;
  home_application: string;
  ecrm_id: string;
  contact_id: string;
  organization_name: string;
  organization_id?: string;
  organization_is_data_provider?: boolean;
  twentyfour_seven_contact: boolean;
  primary_contact: boolean;
  sync_to_ecrm: boolean;
  last_sync_date?: string;
  api_key_id?: string;
  key_display_label?: string;
  asset_manager?: boolean;
  password?: string;
}

export const UserDefaults: User = {
  enel_employee_id: '',
  username: '',
  userId: '',
  userType: '',
  userClass: 'PERSON',
  first_names: {},
  title: '',
  timezone: 'America/New_York',
  last_names: {},
  fullName: '',
  brand: 'ENELX',
  locale: 'en_US',
  email: '',
  phone_number: '',
  twentyfour_seven_contact: false,
  primary_contact: false,
  phones: [],
  emails: [],
  countryCode: '',
  addresses: [],
  status: 'Inactive',
  unit_system: 'IMPERIAL',
  home_application: '',
  ecrm_id: '',
  contact_id: '',
  flex_contact_id: '',
  organization_name: '',
  organization_id: '',
  organization_is_data_provider: false,
  sync_to_ecrm: false,
  last_sync_date: '',
  asset_manager: false,
};

export const DefaultPhoneNumber: Phone = {
  primary: true,
  phone_type: 'WORK',
  extension: '',
  country_code: '',
  phone_number: '',
};

export const DefaultMobilePhoneNumber: Phone = {
  primary: false,
  phone_type: 'MOBILE',
  extension: '',
  country_code: '',
  phone_number: '',
};

export const DefaultAddress: Address = {
  address_1: null,
  address_2: null,
  city: null,
  address_type: null,
  state_province: null,
  postal_code: null,
  country: '',
  primary: true,
};

export const DefaultEmail: Email = {
  primary: true,
  email_type: '',
  email_address: '',
};

export interface UserPost {
  first_names: Record<string, string>;
  last_names: Record<string, string>;
  username: string;
  locale: string;
  timezone: string;
  user_type: string;
  user_class?: string;
  organization: string;
  title: string;
  status?: string;
  unit_system: UnitSystem;
  home_application: string;
  brand?: string;
  ecrm_id?: string;
  contact_id?: string;
  email: string;
  phones?: Phone[];
  addresses?: Address[];
  twentyfour_seven_contact: boolean;
  primary_contact: boolean;
  sync_to_ecrm: boolean;
}

export interface UserPatch {
  federated_applications: [
    {
      application_name: string;
      username: string;
    },
  ];
}

export interface ContactSubscription {
  entity_id: string;
  entity_type: string;
  roles: string[];
}

export interface ContactSubscriptionPost {
  communication_name: string;
  site_id: string;
  entity_type: string;
  contact_methods: string[];
  primary_ind: boolean;
  twentyfour_seven_ind: boolean;
  sync_to_ecrm: boolean;
}
